/**
 * Standardized error handling utilities
 */

import { ERROR_MESSAGES } from './submissionActivityConstants';

/**
 * Extracts a user-friendly error message from various error types
 * @param {Error|string|object} error - The error to extract message from
 * @param {string} fallbackMessage - Default message if extraction fails
 * @returns {string} Clean error message for display
 */
export const extractErrorMessage = (error, fallbackMessage = 'An unexpected error occurred') => {
	// Handle null/undefined
	if (!error) {
		return fallbackMessage;
	}
	
	// Handle string errors
	if (typeof error === 'string') {
		return error.trim() || fallbackMessage;
	}
	
	// Handle Error objects and error-like objects
	if (typeof error === 'object') {
		// Try standard Error properties
		if (error.message && typeof error.message === 'string') {
			return error.message.trim();
		}
		
		// Try API error format
		if (error.error?.message && typeof error.error.message === 'string') {
			return error.error.message.trim();
		}
		
		// Try response data
		if (error.response?.data?.message && typeof error.response.data.message === 'string') {
			return error.response.data.message.trim();
		}
		
		// Try toString() as last resort for objects
		try {
			const stringified = error.toString();
			if (stringified && stringified !== '[object Object]') {
				return stringified.trim();
			}
		} catch (e) {
			// toString() failed, fall through to fallback
		}
	}
	
	return fallbackMessage;
};

/**
 * Handles API-specific errors with context
 * @param {Error} error - The API error
 * @param {string} context - Context for the error (e.g., 'fetching submissions')
 * @returns {string} Contextual error message
 */
export const handleApiError = (error, context = 'API request') => {
	const baseMessage = extractErrorMessage(error);
	
	// Check for specific error types
	if (error?.response?.status === 401) {
		return ERROR_MESSAGES.authRequired;
	}
	
	if (error?.response?.status === 403) {
		return 'Permission denied. Please check your access rights.';
	}
	
	if (error?.response?.status === 404) {
		return 'The requested resource was not found.';
	}
	
	if (error?.response?.status >= 500) {
		return 'Server error. Please try again later.';
	}
	
	// For network errors
	if (error?.code === 'NETWORK_ERROR' || !error?.response) {
		return 'Network error. Please check your connection.';
	}
	
	// Return extracted message with context if it's generic
	if (baseMessage === 'An unexpected error occurred') {
		return `Failed to complete ${context}. Please try again.`;
	}
	
	return baseMessage;
};

/**
 * Logs errors in development with additional context
 * @param {Error} error - The error to log
 * @param {string} context - Context for debugging
 * @param {object} additionalData - Extra data to log
 */
export const logError = (error, context = 'Unknown', additionalData = {}) => {
	if (process.env.NODE_ENV === 'development') {
		console.group(`🚨 Error in ${context}`);
		console.error('Error object:', error);
		console.error('Error message:', extractErrorMessage(error));
		if (Object.keys(additionalData).length > 0) {
			console.error('Additional context:', additionalData);
		}
		console.groupEnd();
	}
};